import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { API } from "@/config";

export const NEEDS_OPTIONS = [
  "Marketing Strategy", "Content Production", "Paid Advertising", "Website Development",
  "Funnels", "CRM", "AI Automation", "Lead Generation", "Other",
];

export const BUDGET_OPTIONS = [
  "Under $1,000", "$1,000–$2,500", "$2,500–$5,000", "$5,000–$10,000", "$10,000+",
];

export const getSource = (fallback) => {
  const p = new URLSearchParams(window.location.search);
  const utm = ["utm_source", "utm_medium", "utm_campaign"].map((k) => p.get(k)).filter(Boolean);
  return utm.length ? `${fallback} | ${utm.join(" / ")}` : fallback;
};

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", business_name: "", email: "", phone: "", website: "",
    industry: "", needs: [], budget: "", goals: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleNeed = (n) =>
    setForm({ ...form, needs: form.needs.includes(n) ? form.needs.filter((x) => x !== n) : [...form.needs, n] });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: getSource("website-growth-audit") });
      setDone(true);
      toast.success("Request received. We'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden border-t border-line px-6 py-28 md:px-12 md:py-40">
      <div className="absolute -left-40 top-1/3 h-96 w-96 bg-crimson/10 blur-[140px]" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-[1600px] gap-20 lg:grid-cols-2">
        <div>
          <Reveal><Overline>11 — Final Call</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter text-white sm:text-5xl lg:text-7xl">
              Let&rsquo;s build your growth system.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-zinc-400 md:text-lg">
              Whether you need better creative, more qualified leads, a stronger website,
              automated follow-up, or an entire customer acquisition system — we&rsquo;ll
              identify the gaps and build the infrastructure to move your business forward.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <button
              data-testid="contact-book-call-btn"
              onClick={() => navigate("/book")}
              className="mt-12 bg-white px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-crimson hover:text-white"
            >
              Book a Strategy Call
            </button>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
              Or request a growth audit with the form
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          {done ? (
            <div data-testid="contact-success" className="flex h-full min-h-[400px] flex-col items-start justify-center border border-line bg-surface p-12">
              <span className="h-2 w-2 animate-pulse bg-crimson" />
              <h3 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tight text-white">Request received.</h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
                Your growth audit request is in. Our team will review it and respond within
                one business day. Check your inbox for a confirmation.
              </p>
              <button
                data-testid="contact-success-book-btn"
                onClick={() => navigate("/book")}
                className="mt-10 border border-white/30 px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
              >
                Book a Strategy Call Instead
              </button>
            </div>
          ) : (
            <form data-testid="lead-form" onSubmit={submit} className="border border-line bg-surface p-8 md:p-12">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Request a Growth Audit</p>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <input data-testid="lead-name-input" required placeholder="Name *" value={form.name} onChange={set("name")} className="tpa-input" />
                <input data-testid="lead-business-input" placeholder="Business Name" value={form.business_name} onChange={set("business_name")} className="tpa-input" />
                <input data-testid="lead-email-input" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className="tpa-input" />
                <input data-testid="lead-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className="tpa-input" />
                <input data-testid="lead-website-input" placeholder="Website" value={form.website} onChange={set("website")} className="tpa-input" />
                <input data-testid="lead-industry-input" placeholder="Industry" value={form.industry} onChange={set("industry")} className="tpa-input" />
              </div>

              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">What do you need help with?</p>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="lead-needs-group">
                {NEEDS_OPTIONS.map((n) => (
                  <button
                    type="button"
                    key={n}
                    data-testid={`lead-need-${n.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => toggleNeed(n)}
                    aria-pressed={form.needs.includes(n)}
                    className={`border px-3.5 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors duration-300 ${
                      form.needs.includes(n)
                        ? "border-crimson bg-crimson/10 text-white"
                        : "border-line text-zinc-500 hover:border-zinc-400 hover:text-zinc-300"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <select
                data-testid="lead-budget-select"
                value={form.budget}
                onChange={set("budget")}
                className="tpa-input mt-8 appearance-none bg-surface"
              >
                <option value="" disabled>Estimated monthly marketing / growth budget</option>
                {BUDGET_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>

              <textarea
                data-testid="lead-goals-textarea"
                rows={4}
                placeholder="Tell us what you're trying to accomplish."
                value={form.goals}
                onChange={set("goals")}
                className="tpa-input mt-5 resize-none"
              />

              <button
                data-testid="lead-submit-btn"
                type="submit"
                disabled={submitting}
                className="mt-8 w-full bg-white px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-crimson hover:text-white disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Request My Strategy Call"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
